import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu as MenuIcon, X, MessageCircle, Instagram, Clock, MapPin } from "lucide-react";
import { SITE, NAV, HOURS, BRANCHES } from "../data/site";

export function Seal({ className = "", label = "旨" }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-flex items-center justify-center bg-hanko text-paper font-display font-bold shadow-[3px_3px_0_rgba(24,24,27,0.9)] ${className}`}
    >
      {label}
    </span>
  );
}

export function VertKanji({ text, className = "" }) {
  return (
    <span aria-hidden="true" className={`v-text font-display select-none ${className}`}>
      {text}
    </span>
  );
}

export function SectionHead({ overline, title, jp, className = "" }) {
  return (
    <div className={`relative ${className}`}>
      {jp ? (
        <VertKanji
          text={jp}
          className="absolute -top-2 right-0 hidden md:block text-4xl text-ink/10 leading-none"
        />
      ) : null}
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-soy">
        <span aria-hidden="true" className="inline-block h-2 w-2 bg-hanko" />
        {overline}
      </p>
      <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-ink">{title}</h2>
    </div>
  );
}

export function Marquee({ items }) {
  const row = (ariaHidden) => (
    <div aria-hidden={ariaHidden} className="flex shrink-0 items-center">
      {items.map((item, i) => (
        <span key={i} className="flex items-center">
          <span className="px-6 font-display text-lg sm:text-xl font-semibold tracking-wide text-paper">{item}</span>
          <span aria-hidden="true" className="text-salmon">・</span>
        </span>
      ))}
    </div>
  );
  return (
    <div className="overflow-hidden border-y-2 border-ink bg-ink py-4">
      <div className="marquee-track flex w-max">
        {row(false)}
        {row(true)}
      </div>
    </div>
  );
}

export function Header() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 border-b border-sand bg-paper/95 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:h-20 lg:px-12">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3" aria-label="Umaku Sushi — Beranda">
          <Seal className="h-9 w-9 text-lg" />
          <span className="leading-tight">
            <span className="block font-display text-lg font-bold tracking-tight text-ink">Umaku Sushi</span>
            <span className="block text-[10px] font-semibold uppercase tracking-[0.25em] text-soy">
              Kedai Sushi · Halal MUI
            </span>
          </span>
        </Link>
        <nav aria-label="Navigasi utama" className="hidden items-center gap-7 lg:flex">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={item.testId}
              className={({ isActive }) =>
                `text-sm font-semibold transition-colors duration-200 hover:text-hanko ${
                  isActive ? "text-hanko underline decoration-2 underline-offset-8" : "text-ink"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <a
            href={SITE.whatsappBintaro}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-cta"
            className="inline-flex items-center gap-2 bg-hanko px-4 py-2.5 text-sm font-bold text-paper transition-colors duration-200 hover:bg-[#a82719]"
          >
            <MessageCircle className="h-4 w-4" aria-hidden="true" />
            Pesan via WhatsApp
          </a>
        </nav>
        <button
          type="button"
          data-testid="mobile-menu-toggle"
          aria-expanded={open}
          aria-controls="mobile-menu"
          aria-label={open ? "Tutup menu navigasi" : "Buka menu navigasi"}
          onClick={() => setOpen((v) => !v)}
          className="inline-flex h-11 w-11 items-center justify-center border border-sand text-ink lg:hidden"
        >
          {open ? <X className="h-5 w-5" aria-hidden="true" /> : <MenuIcon className="h-5 w-5" aria-hidden="true" />}
        </button>
      </div>
      {open ? (
        <div id="mobile-menu" data-testid="mobile-menu" className="border-t border-sand bg-paper lg:hidden">
          <nav aria-label="Navigasi seluler" className="mx-auto flex max-w-7xl flex-col px-4 py-4 sm:px-6">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`mobile-${item.testId}`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `border-b border-sand/70 py-3.5 text-base font-semibold ${
                    isActive ? "text-hanko" : "text-ink"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <a
              href={SITE.whatsappBintaro}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-whatsapp-cta"
              className="mt-4 inline-flex items-center justify-center gap-2 bg-hanko px-4 py-3 text-sm font-bold text-paper"
            >
              <MessageCircle className="h-4 w-4" aria-hidden="true" />
              Pesan via WhatsApp
            </a>
          </nav>
        </div>
      ) : null}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="border-t-2 border-ink bg-ink text-paper" data-testid="site-footer">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:grid-cols-2 sm:px-6 lg:grid-cols-4 lg:px-12">
        <div>
          <div className="flex items-center gap-3">
            <Seal className="h-9 w-9 text-lg shadow-[3px_3px_0_rgba(251,249,245,0.25)]" />
            <span className="font-display text-xl font-bold">Umaku Sushi</span>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-paper/70">{SITE.tagline}</p>
          <a
            href={SITE.instagram}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-instagram-link"
            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-salmon transition-colors hover:text-paper"
          >
            <Instagram className="h-4 w-4" aria-hidden="true" />
            {SITE.instagramHandle}
          </a>
        </div>
        <nav aria-label="Navigasi footer">
          <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-salmon">Jelajahi</h3>
          <ul className="mt-4 space-y-2.5">
            {NAV.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  data-testid={`footer-${item.testId}`}
                  className="text-sm text-paper/80 transition-colors hover:text-paper"
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <div>
          <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-salmon">Cabang Kami</h3>
          <ul className="mt-4 space-y-2.5">
            {BRANCHES.map((b) => (
              <li key={b.id} className="text-sm text-paper/80">
                <Link to="/lokasi/" data-testid={`footer-branch-${b.id}`} className="transition-colors hover:text-paper">
                  {b.area} — {b.city}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-salmon">Jam Buka</h3>
          <p className="mt-4 flex items-start gap-2 text-sm text-paper/80">
            <Clock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>
              {HOURS.days}
              <br />
              {HOURS.time}
              <br />
              <span className="text-paper/60">{HOURS.closed}</span>
            </span>
          </p>
          <p className="mt-4 flex items-start gap-2 text-sm text-paper/80">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>Jabodetabek, Indonesia</span>
          </p>
        </div>
      </div>
      <div className="border-t border-paper/15">
        <div className="mx-auto flex max-w-7xl flex-col gap-2 px-4 py-5 text-xs text-paper/50 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-12">
          <p>© 2026 Umaku Sushi · Bersertifikat Halal MUI</p>
          <p>Foto pada situs ini merupakan ilustrasi hidangan.</p>
        </div>
      </div>
    </footer>
  );
}
